/**
 * Projects screen — issue #37 + #50.
 *
 * Portfolio view: table of all projects with health status, phase,
 * milestone progress and budget summary. Row click navigates to detail.
 *
 * #50 (A5): Delays band — fetches GET /agents/development/delays and renders
 * an alert band above the table if any projects have overdue/at-risk milestones.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAINative } from "../providers/AINativeProvider.js";
import type { ProjectSummary, ProjectPhase, HealthStatus } from "./projects/types.js";
import { formatCurrency, healthClass, healthLabel } from "./projects/utils.js";
import "./projects/projects.css";

// ─── API adapter ──────────────────────────────────────────────────────────────

/** The raw project row shape returned by GET /projects (backend #30). */
interface RawProject {
  entity_id?: string;
  project_id?: string;
  id?: string;
  name?: string;
  phase?: string;
  status?: string;
  health?: string;
  address?: string;
  milestoneProgress?: number;
  milestoneCount?: number;
  milestoneDone?: number;
  budget?: { total: number; spent: number; variance: number };
}

/** Map a raw API project onto the ProjectSummary the table renders. Guarantees a
 *  stable unique `id` (used as the React key + row navigation target). */
function toProjectSummary(p: RawProject): ProjectSummary {
  // Prefer entity_id: the detail endpoint (GET /projects/:id) resolves by the
  // canonical `project:{id}` entity_id, NOT the bare project_id (which 404s).
  const id = p.entity_id ?? p.id ?? p.project_id ?? crypto.randomUUID();
  return {
    id,
    name: p.name ?? id,
    ...(p.address ? { address: p.address } : {}),
    phase: (p.phase ?? "acquisition") as ProjectPhase,
    status: p.status ?? "active",
    health: (p.health ?? "green") as HealthStatus,
    milestoneProgress: p.milestoneProgress ?? 0,
    milestoneCount: p.milestoneCount ?? 0,
    milestoneDone: p.milestoneDone ?? 0,
    ...(p.budget ? { budget: p.budget } : {}),
  };
}

// ─── Delays band types ────────────────────────────────────────────────────────

/** Shape of a single delay entry from GET /agents/development/delays */
interface DelayEntry {
  projectId: string;
  projectName: string;
  overdueMilestones: unknown[];
  atRiskMilestones?: unknown[];
  healthColor?: string;
}

interface DelaysResponse {
  delays: DelayEntry[];
  count?: number;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function HealthBadge({ health }: { health: ProjectSummary["health"] }) {
  return (
    <span
      className={`health-badge ${healthClass(health)}`}
      aria-label={`Health: ${healthLabel(health)}`}
    >
      <span className="health-dot" aria-hidden="true" />
      {healthLabel(health)}
    </span>
  );
}

function ProgressCell({
  done,
  total,
  health,
}: {
  done: number;
  total: number;
  health: ProjectSummary["health"];
}) {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  const fillClass =
    health === "red"
      ? "progress-bar__fill--red"
      : health === "yellow"
        ? "progress-bar__fill--yellow"
        : "progress-bar__fill--green";

  return (
    <div className="projects-table__progress-wrap">
      <div
        className="progress-bar"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${done} of ${total} milestones complete`}
      >
        <div
          className={`progress-bar__fill ${fillClass}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="projects-table__progress-label">
        {done}/{total} milestones · {pct}%
      </span>
    </div>
  );
}

function BudgetCell({ budget }: { budget: ProjectSummary["budget"] }) {
  if (!budget) return <span className="projects-table__budget">—</span>;

  const isOver = budget.variance > 0;
  return (
    <div>
      <div className={`projects-table__budget ${isOver ? "projects-table__budget-over" : ""}`}>
        {formatCurrency(budget.total)}
      </div>
      {isOver && (
        <div className="projects-table__budget projects-table__budget-over">
          +{formatCurrency(budget.variance)} over
        </div>
      )}
    </div>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 4 }, (_, i) => (
        <tr key={i} aria-hidden="true">
          <td className="projects-table__cell" colSpan={5}>
            <div className="skeleton skeleton-row" />
          </td>
        </tr>
      ))}
    </>
  );
}

function StatsBar({ projects }: { projects: ProjectSummary[] }) {
  const active = projects.filter((p) => p.status !== "completed").length;
  const atRisk = projects.filter((p) => p.health !== "green").length;
  const totalBudget = projects.reduce((sum, p) => sum + (p.budget?.total ?? 0), 0);

  return (
    <div className="stats-bar" role="group" aria-label="Portfolio summary">
      <div className="stat-card">
        <div className="stat-card__value">{projects.length}</div>
        <div className="stat-card__label">Total Projects</div>
      </div>
      <div className="stat-card">
        <div className="stat-card__value">{active}</div>
        <div className="stat-card__label">Active</div>
      </div>
      <div className="stat-card">
        <div className="stat-card__value" style={{ color: atRisk > 0 ? "var(--c-yellow)" : undefined }}>
          {atRisk}
        </div>
        <div className="stat-card__label">Need Attention</div>
      </div>
      {totalBudget > 0 && (
        <div className="stat-card">
          <div className="stat-card__value">{formatCurrency(totalBudget)}</div>
          <div className="stat-card__label">Total Budget</div>
        </div>
      )}
    </div>
  );
}

/** A5 — Development Agent delays alert band. */
function DelaysBand({
  delays,
  onProjectClick,
}: {
  delays: DelayEntry[];
  onProjectClick: (id: string) => void;
}) {
  if (delays.length === 0) return null;

  const totalOverdue = delays.reduce((sum, d) => sum + d.overdueMilestones.length, 0);
  const totalAtRisk = delays.reduce((sum, d) => sum + (d.atRiskMilestones?.length ?? 0), 0);

  const parts: string[] = [];
  if (totalOverdue > 0) parts.push(`${totalOverdue} overdue`);
  if (totalAtRisk > 0) parts.push(`${totalAtRisk} at-risk`);
  const milestoneSummary = parts.length > 0 ? ` (${parts.join(", ")} milestone${totalOverdue + totalAtRisk !== 1 ? "s" : ""})` : "";

  return (
    <div className="delays-band" role="alert" aria-label="Development Agent: project delays">
      <div className="delays-band__header">
        <span className="delays-band__icon" aria-hidden="true">⚠</span>
        <span className="delays-band__title">
          {delays.length} project{delays.length !== 1 ? "s" : ""} {delays.length !== 1 ? "have" : "has"} overdue or at-risk milestones{milestoneSummary}
        </span>
      </div>
      <ul className="delays-band__list" aria-label="Affected projects">
        {delays.map((d) => (
          <li key={d.projectId} className="delays-band__item">
            <button
              className="delays-band__link"
              onClick={() => onProjectClick(d.projectId)}
              aria-label={`View project: ${d.projectName}`}
            >
              {d.projectName}
            </button>
            {d.overdueMilestones.length > 0 && (
              <span className="delays-band__tag delays-band__tag--overdue">
                {d.overdueMilestones.length} overdue
              </span>
            )}
            {(d.atRiskMilestones?.length ?? 0) > 0 && (
              <span className="delays-band__tag delays-band__tag--at-risk">
                {d.atRiskMilestones!.length} at risk
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── #44: New Project create form ─────────────────────────────────────────────

// Backend-accepted lifecycle phases (see apps/api ProjectsService.PROJECT_PHASES).
// NOTE: the frontend `ProjectPhase` display type is a divergent/legacy enum
// (pre-development/closeout/completed); the create API validates against THIS set,
// so we send these literal values, not the display type.
const NEW_PROJECT_PHASES = [
  "acquisition",
  "design",
  "permitting",
  "construction",
  "completion",
] as const;
type NewProjectPhase = (typeof NEW_PROJECT_PHASES)[number];

/** Raw shape of POST /projects — { project: { entity_id, ... } }. */
interface CreatedProjectResponse {
  project?: { entity_id?: string; project_id?: string; id?: string; name?: string };
}

function NewProjectForm({
  onCreated,
  onCancel,
  api,
}: {
  onCreated: (routeId: string) => void;
  onCancel: () => void;
  api: ReturnType<typeof useAINative>["api"];
}) {
  const [name, setName] = useState("");
  const [phase, setPhase] = useState<NewProjectPhase>("acquisition");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const resp = await api.call<CreatedProjectResponse>("/projects", {
        method: "POST",
        body: {
          name: trimmed,
          phase,
          status: "active",
        },
      });
      const created = resp.project;
      // The detail route resolves by entity_id (project:{id}); prefer it.
      const routeId =
        created?.entity_id ?? created?.project_id ?? created?.id ?? "";
      onCreated(routeId);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Failed to create project");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="new-project-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Create a new project"
    >
      <div className="new-project-modal__backdrop" onClick={onCancel} aria-hidden="true" />
      <div className="new-project-modal__card">
        <h2 className="new-project-modal__title">New Project</h2>

        <label className="new-project-form__label" htmlFor="np-name">
          Project name
        </label>
        <input
          id="np-name"
          type="text"
          className="new-project-form__input"
          placeholder="e.g. Riverside Mixed-Use"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={submitting}
          aria-label="Project name"
          autoFocus
        />

        <label className="new-project-form__label" htmlFor="np-phase">
          Starting phase
        </label>
        <select
          id="np-phase"
          className="new-project-form__select"
          value={phase}
          onChange={(e) => setPhase(e.target.value as NewProjectPhase)}
          disabled={submitting}
          aria-label="Starting phase"
        >
          {NEW_PROJECT_PHASES.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>

        {errorMsg && (
          <div className="error-banner" role="alert" style={{ marginTop: 10 }}>
            <span className="error-banner__icon">⚠</span>
            <span className="error-banner__text">{errorMsg}</span>
          </div>
        )}

        <div className="new-project-form__actions">
          <button
            className="new-project-form__cancel"
            onClick={onCancel}
            disabled={submitting}
            aria-label="Cancel"
          >
            Cancel
          </button>
          <button
            className="new-project-form__submit"
            onClick={() => { void handleSubmit(); }}
            disabled={submitting || !name.trim()}
            aria-label="Create project"
          >
            {submitting ? "Creating..." : "Create Project"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function ProjectsPage() {
  const { api } = useAINative();
  const navigate = useNavigate();

  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // A5: delays state — separate fetch, degrade gracefully
  const [delays, setDelays] = useState<DelayEntry[]>([]);

  // #44: New Project modal
  const [showNewProject, setShowNewProject] = useState(false);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.call<unknown>("/projects");
      // Accept either a plain array or { projects: [] } envelope
      const rawList: RawProject[] = Array.isArray(data)
        ? (data as RawProject[])
        : ((data as { projects?: RawProject[] })?.projects ?? []);
      // Map the live API shape (entity_id / project_id, no `id`) onto ProjectSummary
      // so every row has a stable unique `id` (React key) and the table renders.
      setProjects(rawList.map(toProjectSummary));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load projects";
      // Gracefully handle unavailable endpoint — treat as empty, show soft error
      setError(msg);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // A5: fetch delays from development agent; never blocks or errors the main page
  const fetchDelays = useCallback(async () => {
    try {
      const data = await api.call<DelaysResponse>("/agents/development/delays");
      setDelays(data?.delays ?? []);
    } catch {
      // Degrade gracefully — delays band simply stays hidden
      setDelays([]);
    }
  }, [api]);

  useEffect(() => {
    void fetchProjects();
    void fetchDelays();
  }, [fetchProjects, fetchDelays]);

  // Merge the Development Agent's delays data into each project's health so the
  // "Need Attention" count and per-row health reflect real overdue/at-risk
  // milestones. GET /projects does not return a computed `health` (it's null),
  // so without this the whole portfolio shows as green even when the delays band
  // reports 13 projects with overdue milestones.
  const enrichedProjects = useMemo<ProjectSummary[]>(() => {
    if (delays.length === 0) return projects;
    const byProject = new Map<string, DelayEntry>();
    for (const d of delays) byProject.set(d.projectId, d);
    return projects.map((p) => {
      const delay = byProject.get(p.id);
      if (!delay) return p;
      const overdue = delay.overdueMilestones?.length ?? 0;
      const atRiskCount = delay.atRiskMilestones?.length ?? 0;
      // Prefer the agent's computed healthColor; else derive from counts.
      const health: HealthStatus = (delay.healthColor as HealthStatus | undefined)
        ?? (overdue > 0 ? "red" : atRiskCount > 0 ? "yellow" : p.health);
      return { ...p, health };
    });
  }, [projects, delays]);

  // #44: after a project is created, close the modal and either navigate to the
  // new project's detail (if we got an id) or refresh the list.
  const handleProjectCreated = useCallback(
    (routeId: string) => {
      setShowNewProject(false);
      if (routeId) {
        void navigate(`/projects/${encodeURIComponent(routeId)}`);
      } else {
        void fetchProjects();
      }
    },
    [navigate, fetchProjects],
  );

  const handleRowClick = (id: string) => {
    void navigate(`/projects/${id}`);
  };

  const handleRowKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      void navigate(`/projects/${id}`);
    }
  };

  /** Navigate to a project from the delays band — ids may be entity refs like "project:xxx" */
  const handleDelayProjectClick = (projectId: string) => {
    // Strip "project:" prefix if present to get the route id
    const routeId = projectId.startsWith("project:") ? projectId : projectId;
    void navigate(`/projects/${routeId}`);
  };

  return (
    <main className="projects-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="page-subtitle">Portfolio · Health · Milestones · Risks · Budget</p>
        </div>
        <button
          className="new-project-btn"
          onClick={() => setShowNewProject(true)}
          aria-label="Create a new project"
        >
          + New Project
        </button>
      </div>

      {showNewProject && (
        <NewProjectForm
          onCreated={handleProjectCreated}
          onCancel={() => setShowNewProject(false)}
          api={api}
        />
      )}

      {/* A5: Delays band — rendered above the table if agent data is available */}
      {delays.length > 0 && (
        <DelaysBand delays={delays} onProjectClick={handleDelayProjectClick} />
      )}

      {error && (
        <div className="error-banner" role="alert">
          <span className="error-banner__icon">⚠</span>
          <span className="error-banner__text">{error}</span>
          <button className="error-banner__retry" onClick={() => void fetchProjects()}>
            Retry
          </button>
        </div>
      )}

      {!loading && !error && enrichedProjects.length > 0 && (
        <StatsBar projects={enrichedProjects} />
      )}

      <div className="table-wrapper">
        <table className="projects-table" aria-label="Projects portfolio">
          <thead className="projects-table__head">
            <tr>
              <th scope="col">Project</th>
              <th scope="col">Health</th>
              <th scope="col">Phase</th>
              <th scope="col">Milestones</th>
              <th scope="col">Budget</th>
            </tr>
          </thead>
          <tbody>
            {loading && <SkeletonRows />}

            {!loading && projects.length === 0 && (
              <tr>
                <td colSpan={5}>
                  <div className="empty-state" style={{ margin: "0", border: "none" }}>
                    <div className="empty-state__icon">🏗️</div>
                    <div className="empty-state__title">No projects yet</div>
                    <div className="empty-state__desc">
                      Projects created in the system will appear here with their health status,
                      milestone progress, and budget overview.
                    </div>
                  </div>
                </td>
              </tr>
            )}

            {!loading &&
              enrichedProjects.map((project) => (
                <tr
                  key={project.id}
                  className="projects-table__row"
                  onClick={() => handleRowClick(project.id)}
                  onKeyDown={(e) => handleRowKeyDown(e, project.id)}
                  tabIndex={0}
                  role="button"
                  aria-label={`Open project: ${project.name}`}
                >
                  <td className="projects-table__cell">
                    <span className="projects-table__name">{project.name}</span>
                    {project.address && (
                      <span className="projects-table__address">{project.address}</span>
                    )}
                  </td>
                  <td className="projects-table__cell">
                    <HealthBadge health={project.health} />
                  </td>
                  <td className="projects-table__cell">
                    <span className="status-chip">{project.phase}</span>
                  </td>
                  <td className="projects-table__cell">
                    <ProgressCell
                      done={project.milestoneDone}
                      total={project.milestoneCount}
                      health={project.health}
                    />
                  </td>
                  <td className="projects-table__cell">
                    <BudgetCell budget={project.budget} />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </main>
  );
}
